<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="wc-question">
        <span class="wc-bar wc-bar--xl"></span>
      </div>
      <div class="wc-cloud">
        <span
          v-for="(w, i) in words"
          :key="w.text"
          class="wc-word"
          :class="['--size-' + w.size, { '--accent': w.accent, '--landing': i === 0 }]"
          :style="{ '--word-i': i, '--x': w.x + '%', '--y': w.y + '%' }"
        >{{ w.text }}</span>
      </div>
    </template>

    <template #audience>
      <span class="wc-phone-q"></span>
      <div class="wc-phone-input">
        <span class="wc-phone-typed">excited<span class="wc-caret">|</span></span>
      </div>
      <button type="button" class="wc-phone-send" tabindex="-1">
        <span class="material-symbols-rounded">send</span>
        Send
      </button>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

type Word = { text: string; size: number; x: number; y: number; accent?: boolean }

export default Vue.extend({
  name: 'PreviewWordCloud',
  components: { PreviewFrame },
  props: {
    slide: { type: Object as PropType<SlideType>, required: true },
  },
  data() {
    return {
      words: [
        {
          text: 'excited', size: 4, x: 50, y: 50, accent: true,
        },
        {
          text: 'curious', size: 3, x: 18, y: 30,
        },
        {
          text: 'hopeful', size: 2, x: 78, y: 28,
        },
        {
          text: 'focused', size: 3, x: 22, y: 72,
        },
        {
          text: 'ready', size: 2, x: 76, y: 74,
        },
        {
          text: 'calm', size: 1, x: 50, y: 18,
        },
      ] as Word[],
    }
  },
})
</script>

<style scoped lang="scss">
.wc-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.2);
}
.wc-bar--xl { width: 70%; }

.wc-question {
  padding-bottom: 6px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.wc-cloud {
  position: relative;
  flex: 1;
  min-height: 130px;
}
.wc-word {
  position: absolute;
  top: var(--y); left: var(--x);
  transform: translate(-50%, -50%);
  font-weight: 700;
  color: rgba(15, 15, 20, 0.55);
  white-space: nowrap;
}
.wc-word.--size-1 { font-size: 10px; }
.wc-word.--size-2 { font-size: 13px; }
.wc-word.--size-3 { font-size: 17px; }
.wc-word.--size-4 { font-size: 22px; }
.wc-word.--accent { color: var(--accent); }
.wc-word.--landing {
  animation: wc-land 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}

/* ---- Audience phone ---- */
.wc-phone-q {
  display: block; height: 8px; border-radius: 999px;
  width: 70%; background: rgba(15, 15, 20, 0.18);
  margin-bottom: 4px;
}
.wc-phone-input {
  border: 1px solid color-mix(in srgb, var(--accent) 50%, transparent);
  border-radius: 8px;
  padding: 12px 10px;
  min-height: 44px;
  background: white;
}
.wc-phone-typed {
  font-size: 14px; font-weight: 700;
  color: rgba(15, 15, 20, 0.85);
}
.wc-caret {
  color: var(--accent);
  animation: wc-blink 1s steps(2) infinite;
}
.wc-phone-send {
  appearance: none; border: none;
  background: var(--accent); color: white;
  font-family: inherit; font-weight: 700; font-size: 12px;
  border-radius: 8px;
  padding: 8px 12px;
  display: flex; align-items: center; justify-content: center; gap: 4px;
  cursor: pointer;
  animation: wc-tap 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}
.wc-phone-send .material-symbols-rounded { font-size: 14px; }

@keyframes wc-land {
  0%, 12% { transform: translate(-50%, -50%) scale(0.4); opacity: 0; }
  35%     { transform: translate(-50%, -50%) scale(1.15); opacity: 1; }
  50%, 92% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
  100%    { transform: translate(-50%, -50%) scale(1); opacity: 1; }
}
@keyframes wc-blink {
  0% { opacity: 1; } 100% { opacity: 0; }
}
@keyframes wc-tap {
  0%, 60%   { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 35%, transparent); }
  72%       { box-shadow: 0 0 0 8px color-mix(in srgb, var(--accent) 0%, transparent); }
  100%      { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent); }
}

@media (prefers-reduced-motion: reduce) {
  .wc-word.--landing { animation: none; }
  .wc-caret, .wc-phone-send { animation: none; }
}
</style>
